import { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Skeleton from "react-loading-skeleton";
import clsx from "clsx";
import { Tooltip } from "flowbite-react";

import { useCollection, useResource } from "@/hooks/resources";
import MenuPageLayout from "@/components/layouts/MenuPageLayout";
import { useAppContext } from "@/contexts/app-context";
import { Button } from "@/components/button";
import { request } from "@/utils/request";
import ProgressBar from "@/components/ProgressBar";
import SongType from "@/types/song-type";
import SongPreference from "@/types/song-preference";
import ScoreBalls from "@/components/songs/score-balls";
import preAssessmentComplete from "@/assets/pre-assessment-complete.png";
import postAssessmentComplete from "@/assets/post-assessment-complete.png";

import "react-loading-skeleton/dist/skeleton.css";

const Songs = () => {
  const {
    state: {
      currentUser,
      isLoadingCurrentUser,
      hasActiveSubscription,
      selectedChildId,
    },
  } = useAppContext();

  const [songs, { isLoading: isLoadingSongs }] =
    useCollection<SongType>("/songs");
  const [songProgresses] = useResource<{ [songId: number]: number }>(
    `/songs/progress?child_id=${selectedChildId}`,
  );
  const [songAssessments] = useResource<{
    [songId: number]: { preAssessment: number; postAssessment: number };
  }>(`/songs/assessments?child_id=${selectedChildId}`);
  const [songPreferences] = useCollection<SongPreference>(
    `/song_preferences?child_id=${selectedChildId}`,
    { enabled: selectedChildId !== undefined },
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubscribe = async () => {
    setIsSubmitting(true);
    const response = await request.post<{ redirectUrl: string }>(
      "/stripe/checkout/sessions",
      {
        cancelUrl: window.location.href,
      },
    );

    if (response.ok && response.data?.redirectUrl) {
      window.location.href = response.data.redirectUrl;
    }

    setIsSubmitting(false);
  };

  return (
    <MenuPageLayout title="Songs" className="relative overflow-clip">
      <div className="grid grid-cols-2 sm:flex flex-wrap justify-center gap-4 w-full">
        {!isLoadingSongs
          ? (songs || []).map((song) => (
              <div
                key={song.id}
                className="sm:w-[220px] rounded overflow-clip border bg-gray-50 flex flex-col relative"
              >
                {songAssessments &&
                  (songAssessments[song.id]?.preAssessment || 0) >= 6 && (
                    <div className="absolute top-3 left-3">
                      <Tooltip content="Pre-Assessment Completed">
                        <img
                          src={preAssessmentComplete}
                          className="block h-5"
                        />
                      </Tooltip>
                    </div>
                  )}

                {songAssessments &&
                  (songAssessments[song.id]?.postAssessment || 0) >= 6 && (
                    <div className="absolute top-3 right-3">
                      <Tooltip content="Post-Assessment Completed">
                        <img src={postAssessmentComplete} className="h-5" />
                      </Tooltip>
                    </div>
                  )}

                <div className="p-4 flex flex-col gap-4 items-center relative">
                  <img
                    src={song.image.url}
                    className="rounded-full size-24 sm:size-32 bg-gray-200 p-2"
                  />

                  <h4 className="text-center">{song.name}</h4>

                  <Link
                    to={`/songs/${song.id}/play`}
                    className="btn-primary w-full"
                  >
                    PLAY
                    <FontAwesomeIcon icon="play" />
                  </Link>

                  <Link to={`/songs/${song.id}`} className="text-blue-500">
                    Preview
                  </Link>

                  {songPreferences?.find((sp) => sp.song.id === song.id)?.assessmentEnabled && (
                    <span className="text-xs text-green-400 font-medium">Assessments Enabled</span>
                  )}

                  <div className="absolute right-2 bottom-2 flex">
                    <ScoreBalls value={song.semanticsScore} type="semantics" />
                    <ScoreBalls
                      value={song.articulationScore}
                      type="articulation"
                    />
                  </div>
                </div>

                <div className="!h-2 border-t bg-gray-200">
                  {songProgresses && songProgresses[song.id] && (
                    <ProgressBar
                      percentage={(songProgresses[song.id] * 100) / 21}
                      color={
                        songProgresses[song.id] >= 21
                          ? "bg-primary-green-400"
                          : "bg-primary-blue-400"
                      }
                      className={clsx({
                        "rounded-r-full": songProgresses[song.id] < 21,
                      })}
                    />
                  )}
                </div>
              </div>
            ))
          : [...Array(15)].map((_, index) => (
              <div
                key={index}
                className="sm:w-[220px] rounded border p-4 flex flex-col gap-4 items-center bg-gray-50"
              >
                <Skeleton
                  circle
                  containerClassName="size-24 sm:size-32 leading-none"
                  className="size-24 sm:size-32"
                />

                <Skeleton
                  containerClassName="w-2/3 leading-none"
                  className="h-6"
                />
                <Skeleton
                  containerClassName="w-full leading-none"
                  className="h-12"
                />
                <Skeleton
                  containerClassName="w-1/2 leading-none"
                  className="h-6"
                />
              </div>
            ))}
      </div>

      {!isLoadingCurrentUser && !hasActiveSubscription && (
        <div className="absolute inset-0 bg-black/25 pt-8 px-4 flex justify-center items-start">
          <div className="bg-white p-8 rounded-lg ring-4 ring-amber-400 flex flex-col gap-y-4 items-center w-[500px] max-w-full text-center">
            {(currentUser?.subscriptions || []).length > 0 ? (
              <>
                <h2 className="font-semibold">Your membership has expired</h2>

                <p>Re-subscribe to continue playing.</p>

                <Button
                  type="button"
                  className="btn-secondary px-8"
                  onClick={handleSubscribe}
                  loading={isSubmitting}
                >
                  Re-subscribe
                </Button>
              </>
            ) : (
              <>
                <h2 className="font-semibold">
                  A membership is required to play
                </h2>

                <p>Subscribe to start playing.</p>

                <Button
                  type="button"
                  className="btn-secondary px-8"
                  onClick={handleSubscribe}
                  loading={isSubmitting}
                >
                  Subscribe
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </MenuPageLayout>
  );
};

export default Songs;
